import React, { useState, useEffect } from 'react';
import { Button, Select, Input, message, Table, Checkbox, Tooltip, Modal, Alert } from 'antd';
import axios from 'axios';
import { TaskData } from '../../../../types/TaskData';
import { ViewType } from '../../../../types/ViewType';
import LoadingModal from "../../../../components/loadingModal/LoadingModal";
import Dragger from "antd/es/upload/Dragger";
import { FileExcelOutlined, FilePdfOutlined } from "@ant-design/icons";
import { UploadFile } from "antd/es/upload/interface";
import { useProjectContext } from "../../../../context/ProjectContext";
import './ColorReservationCreation.css';
import { getConfig } from "../../../../config";
import { useDirtyContext } from "../../../../context/DirtyContext";

// Detail-Modal + Typen
import MaterialReservationDetailModal, {
    MaterialReservationState,
    MaterialReservationSavePayload
} from "../../materialReservationDetail/MaterialReservationDetailModal";

const { Option } = Select;
const { API_BASE_URL } = getConfig();
const AUTH_TOKEN = "VdjCKidks7qTEyuU6QeJtezZ3PeCHhZQ2yHJ3kPtcfTN8BjVqU";

// Minimaltyp für JobCodes (Standalone)
interface NetworkPlanJobCode {
    alias: string;
    heliosApprovedSystemPlan?: string | null;
    colorCode?: string | null;
}

interface ColorReservationCreationProps {
    data: TaskData;
    viewType: ViewType;
    onRefreshTable?: () => void;
    onSave?: (data: any) => void;
    type: "color" | "material";
    projectNo?: string;
    externalSystemPlanLink?: string;
}

const conditions = [
    /4201/,
    /590/,
    /dura.*xal|xal.*dura/,
    /tiefenmatt|tiefmatt/,
    /ncs.*(-\d{4}|\s-\d{4})|(-\d{4}|\s-\d{4}).*ncs/i
];

const ColorReservationCreation: React.FC<ColorReservationCreationProps> = ({
                                                                               data,
                                                                               viewType,
                                                                               onRefreshTable,
                                                                               type,
                                                                               onSave,
                                                                               projectNo,
                                                                               externalSystemPlanLink
                                                                           }) => {
    const { selectedProject: contextProject } = useProjectContext();
    const [suppliers, setSuppliers] = useState<{ name: string; number: string; orderNo: string; }[]>([]);
    const [selectedSupplier, setSelectedSupplier] = useState<string | null>(
        type === "material" ? "1053" : "1000"
    );
    const [systemPlan, setSystemPlan] = useState('');
    const [colorCode, setColorCode] = useState('');
    const [isSpecialColor, setIsSpecialColor] = useState(false);

    // Für COLOR weiterhin im Einsatz; für MATERIAL ungenutzt (wir zeigen keine Felder mehr)
    const [runningMeters, setRunningMeters] = useState('');             // String für Rückwärtskompatibilität
    const [runningMetersKilogramm, setRunningMetersKilogramm] = useState(''); // bleibt leer bei MATERIAL

    const [reservationNote, setReservationNote] = useState('');
    const [orderCards, setOrderCards] = useState<any[]>([]);
    const [selectedCards, setSelectedCards] = useState<number[]>([]);
    const [isPreviewShown, setIsPreviewShown] = useState(false);
    const [jobCodes, setJobCodes] = useState<NetworkPlanJobCode[]>([]);
    const [mappedJobCode, setMappedJobCode] = useState<NetworkPlanJobCode | null>(null);
    const [loadingCount, setLoadingCount] = useState(0);
    const [isGeneratingPreview, setIsGeneratingPreview] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [sendCopyToPL, setSendCopyToPL] = useState(false);
    const { setDirty } = useDirtyContext();
    const [availableColors, setAvailableColors] = useState<string[]>([]);
    const [systemPlanPdfFile, setSystemPlanPdfFile] = useState<UploadFile[]>([]);
    const isPdfValid = systemPlanPdfFile.length === 1 && systemPlanPdfFile[0].name.endsWith('.pdf');
    const [netProductTotalCHF, setNetProductTotalCHF] = useState<number | null>(null);

    // Material-Detail-Modal State (Öffnen + gemerkte Konfiguration)
    const [isMaterialDetailOpen, setMaterialDetailOpen] = useState(false);
    const [materialDetailState, setMaterialDetailState] = useState<MaterialReservationState | null>(null);

    const quantityLabel = React.useMemo(() => {
        const u = materialDetailState?.uom;
        if (!u || u === 'LM') return 'Laufmeter Total';
        if (u === 'PCS_STOREY') return 'Geschosse Total';
        if (u === 'PCS_FACADE') return 'Fassaden Total';
        if (u === 'PCS_BUILDING') return 'Gebäude Total';
        return 'Anzahl';
    }, [materialDetailState?.uom]);

    const projectToUse =
        projectNo && projectNo !== "Alle"
            ? projectNo
            : (data && data.projectNo)
                ? data.projectNo
                : contextProject;

    // Lieferanten laden
    useEffect(() => {
        const fetchSuppliers = async () => {
            try {
                setLoadingCount(prev => prev + 1);
                const response = await axios.get(`${API_BASE_URL}/getAllSurfaceSuppliers?auth=${AUTH_TOKEN}&type=COATING`);
                setSuppliers(response.data);
            } catch {
                message.error('Fehler beim Abrufen der Farblieferanten');
            } finally {
                setLoadingCount(prev => Math.max(0, prev - 1));
            }
        };
        fetchSuppliers();
    }, []);

    // JobCodes laden
    useEffect(() => {
        const fetchJobCodes = async () => {
            try {
                setLoadingCount(prev => prev + 1);
                const response = await axios.get(
                    `${API_BASE_URL}/getNetworkPlanJobCodes?auth=${AUTH_TOKEN}&projectNo=${projectToUse}`
                );
                setJobCodes(response.data);
            } catch {
                message.error('Fehler beim Abrufen der Job-Codes');
            } finally {
                setLoadingCount(prev => Math.max(0, prev - 1));
            }
        };
        fetchJobCodes();
    }, []);

    // OrderCards laden
    useEffect(() => {
        const fetchOrderCards = async () => {
            try {
                setLoadingCount(prev => prev + 1);
                const response = await axios.get(`${API_BASE_URL}/getNetworkPlanOrderCardDataByJobCode?auth=${AUTH_TOKEN}&projectNo=${projectToUse}&jobCode=${data.jobCode}`);
                setOrderCards(response.data);
            } catch {
                message.error('Fehler beim Abrufen der Auftragskarten');
            } finally {
                setLoadingCount(prev => Math.max(0, prev - 1));
            }
        };
        fetchOrderCards();
    }, []);

    // Gesamtwert (CHF) inkl. Rabatt laden
    useEffect(() => {
        const fetchAmount = async () => {
            try {
                setLoadingCount(prev => prev + 1);
                const resp = await axios.get(
                    `${API_BASE_URL}/getNetworkPlanOrderJobCodeAmount?auth=${AUTH_TOKEN}&projectNo=${projectToUse}&jobCode=${data.jobCode}`
                );

                const it = resp?.data; // <— Objekt, kein Array
                if (it && typeof it.totalAmount === 'number' && typeof it.discount === 'number') {
                    // discount ist Prozent
                    const net = it.totalAmount * (1 - it.discount / 100);
                    setNetProductTotalCHF(Number.isFinite(net) ? net : null);
                } else {
                    setNetProductTotalCHF(null);
                }
            } catch {
                message.error('Fehler beim Abrufen des Gesamtwerts (CHF).');
                setNetProductTotalCHF(null);
            } finally {
                setLoadingCount(prev => Math.max(0, prev - 1));
            }
        };
        fetchAmount();
        // falls sich Projekt/Jobcode ändern:
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectToUse, data.jobCode]);

    // Gemappten JobCode + Farben + Systemplan setzen
    useEffect(() => {
        const matchedJobCode = jobCodes.find((code) => code.alias === data.jobCode);
        setMappedJobCode(matchedJobCode || null);

        if (matchedJobCode) {
            setSystemPlan(externalSystemPlanLink ?? matchedJobCode.heliosApprovedSystemPlan ?? '');

            if (matchedJobCode.colorCode) {
                const colors = matchedJobCode.colorCode.split('|').filter(color => color !== "Noch Offen");
                setAvailableColors(colors);
                if (colors.length > 0) {
                    setColorCode(colors[0]);
                    const matchesCondition = conditions.some(regex => regex.test(colors[0]));
                    setIsSpecialColor(matchesCondition);
                } else {
                    setIsSpecialColor(false);
                }
            } else {
                setAvailableColors([]);
                setIsSpecialColor(false);
            }
        }
    }, [data, jobCodes, externalSystemPlanLink]);

    const fileToBase64 = (file: UploadFile): Promise<string | null> => {
        return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = () => resolve(null);
            if (file.originFileObj) reader.readAsDataURL(file.originFileObj);
            else resolve(null);
        });
    };

    const handleInputChange = <T,>(setter: (val: T) => void, value: T) => {
        setter(value);
        setDirty(true);
    };

    const handleColorChange = (val: string) => {
        setColorCode(val);
        setDirty(true);
        const matchesCondition = conditions.some(regex => regex.test(val));
        setIsSpecialColor(matchesCondition);
    };

    const handleCardSelection = (id: number) => {
        setSelectedCards((prev) => prev.includes(id) ? prev.filter(cardId => cardId !== id) : [...prev, id]);
    };

    const calculateValues = () => {
        if (selectedCards.length === 0) return orderCards;
        const totalEstimatedValue = orderCards
            .filter(card => selectedCards.includes(card.id))
            .reduce((sum, card) => sum + parseFloat(card.estimatedValue), 0);
        const lm = runningMeters ? parseFloat(String(runningMeters).replace(',', '.')) : 0;
        const netChfTotal = netProductTotalCHF ?? 0;
        if (!isFinite(totalEstimatedValue) || totalEstimatedValue <= 0) {
            return orderCards.map(card => ({
                ...card,
                calculatedValue: "0.00",
                calculatedTotalChf: "0.00",
                colorCode: card.colorCode || colorCode
            }));
        }
        return orderCards.map(card => {
            const share = parseFloat(card.estimatedValue) / totalEstimatedValue;
            return {
                ...card,
                calculatedValue: selectedCards.includes(card.id) ? (share * lm).toFixed(2) : "0.00",
                calculatedTotalChf: selectedCards.includes(card.id)
                    ? (share * netChfTotal).toFixed(2)
                    : "0.00",
                colorCode: card.colorCode || colorCode,
            };
        });
    };

    const uniqueSelectedColors = new Set(selectedCards.map(id => {
        const card = orderCards.find(card => card.id === id);
        return card?.colorCode || colorCode;
    })).size;

    function handleRowColorChange(id: number, color: string) {
        setOrderCards(prevCards =>
            prevCards.map(card =>
                card.id === id ? { ...card, colorCode: color } : card
            )
        );
    }

    // Spalten: „Berechneter Wert“ nur für COLOR
    const tableColumns = [
        {
            title: 'Montagedatum',
            dataIndex: 'mountingDate',
        },
        {
            title: 'Beschreibung',
            dataIndex: 'description',
        },
        ...(availableColors.length > 1
            ? [{
                title: 'Farbcode',
                dataIndex: 'colorCode',
                render: (_text: string, record: any) => (
                    <Select
                        style={{ width: '100%' }}
                        value={record.colorCode || colorCode}
                        onChange={(value) => handleRowColorChange(record.id, value)}
                    >
                        {availableColors.map((color) => (
                            <Select.Option key={color} value={color}>
                                {color}
                            </Select.Option>
                        ))}
                    </Select>
                ),
            }]
            : []),
        {
            title: 'Schätzwert',
            dataIndex: 'estimatedValue',
        },
        {
            title:
                materialDetailState?.uom === 'PCS_STOREY'
                    ? 'Geschosse'
                    : materialDetailState?.uom === 'PCS_FACADE'
                        ? 'Fassaden'
                        : materialDetailState?.uom === 'PCS_BUILDING'
                            ? 'Gebäude'
                            : 'Laufmeter',
            dataIndex: 'calculatedValue',
        },

        {
            title: 'Wert (CHF)',
            dataIndex: 'calculatedTotalChf',
            render: (v: string) =>
                v
                    ? new Intl.NumberFormat('de-CH', { maximumFractionDigits: 2 }).format(parseFloat(v))
                    : '0.00',
        },

    ];

    // Checkbox-Enable-Regeln:
    // - COLOR: solange keine Laufmeter eingegeben → disable
    // - MATERIAL: solange keine Materialreservation existiert → disable
    const rowSelection = {
        selectedRowKeys: selectedCards,
        onChange: (selectedRowKeys: React.Key[]) => {
            setSelectedCards(selectedRowKeys as number[]);
        },
        getCheckboxProps: (_record: TaskData) => ({
            disabled:
                (type === 'color' && !runningMeters) ||
                (type === 'material' && !materialDetailState),
        }),
    };

    async function openCalc() {
        try {
            const response = await axios.get(`${API_BASE_URL}/getDevisCalculationLink?auth=${AUTH_TOKEN}&projectNo=${projectToUse}`);
            if (response.data) {
                window.open(response.data, '_blank');
            } else {
                message.error('Kein gültiger Link erhalten.');
            }
        } catch (error) {
            message.error('Fehler beim Abrufen der Auftragskarten');
            console.error(error);
        }
    }

    const isButtonEnabled =
        type === "material"
            ? selectedCards.length > 0 && isPdfValid && !!materialDetailState
            : !!runningMeters && runningMeters.trim() !== '' && selectedCards.length > 0 && isPdfValid;

    async function handlePreview() {
        try {
            setIsGeneratingPreview(true);
            const pdfBase64 =
                systemPlanPdfFile[0]?.originFileObj
                    ? await fileToBase64(systemPlanPdfFile[0])
                    : null;
            if (!pdfBase64) {
                message.error("Bitte genau eine Systemplan-PDF hochladen.");
                return;
            }
            const calculatedCards = calculateValues()
                .filter((card: any) => selectedCards.includes(card.id))
                .map((card: any) => ({
                    id: card.id,
                    calculatedValue: card.calculatedValue,
                    colorCode: card.colorCode || colorCode,
                }));
            const payload = {
                supplierNumber: selectedSupplier,
                systemPlan,
                colorCode,
                isSpecialColor,
                systemPlanPdf: pdfBase64,
                runningMeters,
                runningMetersKilogramm,
                reservationNote,
                jobCode: data.jobCode,
                id: data.id,
                selectedOrderCards: calculatedCards,
                sendCopyToPL,
            };
            const response = await axios.post(
                `${API_BASE_URL}/getNetworkPlanColorReservationPDFPreview`,
                payload
            );
            if (response.data?.backendMessage) {
                const base64Data = response.data.backendMessage.split(",")[1];
                const byteCharacters = atob(base64Data);
                const byteNumbers = new Array(byteCharacters.length);
                for (let i = 0; i < byteCharacters.length; i++) {
                    byteNumbers[i] = byteCharacters.charCodeAt(i);
                }
                const byteArray = new Uint8Array(byteNumbers);
                const blob = new Blob([byteArray], { type: "application/pdf" });
                const blobUrl = URL.createObjectURL(blob);
                window.open(blobUrl, "_blank");
                setIsPreviewShown(true);
            } else {
                message.error("Fehler: Keine gültige PDF-Vorschau erhalten.");
            }
        } catch (error) {
            message.error("Fehler beim Abrufen der PDF-Vorschau.");
        } finally {
            setIsGeneratingPreview(false);
        }
    }

    async function handleSave() {
        const pdfBase64 = systemPlanPdfFile[0]?.originFileObj ? await fileToBase64(systemPlanPdfFile[0]) : null;

        const reservationData = {
            supplierNumber: selectedSupplier,
            systemPlan,
            colorCode,
            isSpecialColor,
            runningMeters,
            runningMetersKilogramm,
            reservationNote,
            systemPlanPdf: pdfBase64,
            jobCode: data.jobCode,
            id: data.id,
            selectedOrderCards: calculateValues()
                .filter((card: any) => selectedCards.includes(card.id))
                .map((card: any) => ({
                    id: card.id,
                    calculatedValue: card.calculatedValue,
                    colorCode: card.colorCode || colorCode,
                })),
            sendCopyToPL,
            materialReservationState: materialDetailState ?? null,
        };

        if (viewType === ViewType.NEW_EXTERNAL && onSave) {
            onSave(reservationData);
            setIsPreviewShown(false);
        } else {
            try {
                setIsSaving(true);
                await axios.post(`${API_BASE_URL}/createNewNetworkPlanColorReservation`, reservationData);
                message.success('Reservation wurde erfolgreich gespeichert.');
                onRefreshTable?.();
            } catch (error) {
                message.error('Fehler beim Speichern der Reservation');
            } finally {
                setIsSaving(false);
            }
        }
    }

    return (
        <div className="container">
            <LoadingModal open={loadingCount > 0} text="Daten werden geladen..."/>
            <LoadingModal open={isGeneratingPreview} text="Vorschau wird erstellt..."/>
            <LoadingModal open={isSaving} text="Reservation wird erstellt..."/>

            {type === "color" && (
                <div className="input-group">
                    <label><strong>Auswahl Farblieferant<span style={{color: 'red'}}> *</span></strong></label>
                    <Select
                        value={selectedSupplier || undefined}
                        onChange={setSelectedSupplier}
                        placeholder="Wähle einen Lieferanten"
                        style={{width: '100%'}}
                    >
                        {suppliers.map((supplier) => (
                            <Option key={supplier.number} value={supplier.number}>
                                {supplier.name}
                            </Option>
                        ))}
                    </Select>
                </div>
            )}

            {type === "material" && (
                <div className="input-group">
                    <label><strong>Lieferant</strong></label>
                    <Input placeholder="Gukom, spol. s.r.o." value="Gukom, spol. s.r.o." disabled/>
                </div>
            )}

            <div className="input-group">
                <label><strong>Systemplan</strong></label>
                <Input placeholder="Daten geladen..." value={systemPlan} disabled/>
            </div>

            <div className="input-group">
                <label><strong>PDF des Systemplans<span style={{color: 'red'}}> *</span></strong></label>
                <Dragger
                    fileList={systemPlanPdfFile}
                    accept=".pdf"
                    beforeUpload={() => false}
                    maxCount={1}
                    onChange={({fileList}) => setSystemPlanPdfFile(fileList)}
                    onRemove={() => setSystemPlanPdfFile([])}
                >
                    <p className="ant-upload-drag-icon"><FilePdfOutlined/></p>
                    <p className="ant-upload-text">Nur eine PDF-Datei zulässig</p>
                </Dragger>
            </div>
            {type === "material" && (
                <div
                    className="input-group"
                    style={{display: 'flex', justifyContent: 'flex-end', gap: 8}}
                >
                    <Button type="default" onClick={() => setMaterialDetailOpen(true)}>
                        Materialreservation öffnen
                    </Button>
                    <Button type="default" onClick={openCalc}>
                        Kalk öffnen
                    </Button>
                </div>
            )}
            {type === 'material' && !materialDetailState && (
                <Alert
                    type="info"
                    showIcon
                    message="Bitte erst Material-Konfiguration durchführen"
                    style={{marginBottom: 12}}
                />
            )}
            <div className="input-group">
                <label>
                    <strong>
                        {quantityLabel}<span style={{color: 'red'}}> *</span>
                    </strong>
                </label>
                <div className="input-group" style={{display: "flex", alignItems: "center", gap: 8}}>
                    <Input
                        type="number"
                        placeholder="Gebe die Anzahl der Laufmeter ein"
                        value={runningMeters}
                        onChange={(e) => handleInputChange(setRunningMeters, e.target.value)}
                        style={{flex: 1}}
                        disabled={type === "material"} // <- bei Material readonly (aus Modal)
                    />
                    <Tooltip title="Kalk öffnen">
                        <Button type="text" onClick={openCalc} icon={<FileExcelOutlined/>}/>
                    </Tooltip>
                </div>
            </div>

            <div className="input-group">
                <label>
                    <strong>
                        KG pro Laufmeter<span style={{color: "red"}}> *</span>
                    </strong>
                </label>
                <div className="input-group" style={{display: "flex", alignItems: "center", gap: 8}}>
                    <Input
                        type="number"
                        placeholder="Gebe die KG pro Laufmeter ein"
                        value={runningMetersKilogramm}
                        onChange={(e) => handleInputChange(setRunningMetersKilogramm, e.target.value)}
                        style={{flex: 1}}
                        disabled={type === "material"} // <- bei Material readonly (aus Modal)
                    />
                </div>
            </div>
            <div className="input-group">
                <label><strong>Gesamtwert Produkt (CHF) inkl. Rabatt</strong></label>
                <Input
                    disabled
                    value={
                        netProductTotalCHF == null
                            ? ''
                            : new Intl.NumberFormat('de-CH', {maximumFractionDigits: 2}).format(netProductTotalCHF)
                    }
                />
            </div>

            <div className="input-group">
                <Table
                    dataSource={calculateValues()}
                    columns={tableColumns as any}
                    rowKey="id"
                    pagination={false}
                    rowSelection={{...rowSelection}}
                />
            </div>

            <div className="input-group">
                <label>
                    <strong>
                        {type === "color" ? "Hinweis für Farbreservation" : "Hinweis für Materialreservation"}
                    </strong>
                </label>
                <Input.TextArea
                    rows={3}
                    placeholder="Hinweis eingeben"
                    value={reservationNote}
                    onChange={(e) => handleInputChange(setReservationNote, e.target.value)}
                />
            </div>

            <div className="input-group">
                <Checkbox
                    checked={sendCopyToPL}
                    onChange={(e) => handleInputChange(setSendCopyToPL, e.target.checked)}
                    disabled={loadingCount > 0}
                >
                    <strong>Nur an PL senden</strong>
                </Checkbox>
            </div>

            <div style={{textAlign: 'right'}}>
                <Button
                    type="default"
                    className="preview-button"
                    onClick={handlePreview}
                    disabled={!isButtonEnabled || loadingCount > 0}
                    loading={loadingCount > 0}
                >
                    Vorschau anzeigen
                </Button>
                <Button
                    type="primary"
                    className="action-button"
                    onClick={handleSave}
                    disabled={!isButtonEnabled || !isPreviewShown || loadingCount > 0 || (viewType === ViewType.NEW_EXTERNAL && !isPreviewShown)}
                    loading={loadingCount > 0}
                    style={{marginLeft: 8}}
                >
                    {viewType === ViewType.NEW_EXTERNAL
                        ? "Zwischenspeichern"
                        : `Verbindlich Reservieren${uniqueSelectedColors > 1 ? ` (${uniqueSelectedColors})` : ""}`}
                </Button>
            </div>

            <Modal
                open={isMaterialDetailOpen}
                onCancel={() => setMaterialDetailOpen(false)}
                footer={null}
                width="90vw"
                // styles={{ body: { height: '75vh' } }}  // <-- ENTFERNEN
                destroyOnClose
                title="Materialreservierung – Detailansicht"
            >

                <MaterialReservationDetailModal
                    initialState={materialDetailState}
                    onSave={(payload: MaterialReservationSavePayload) => {
                        // Zustand merken (für "Material zuerst konfigurieren"-Gate)
                        setMaterialDetailState(payload.state);

                        // Laufmeter & kg/Lm aus dem Submenü IMMER übernehmen:
                        const lm = Number.isFinite(payload.runningMeters) ? payload.runningMeters.toFixed(2) : '';
                        const kgLm = Number.isFinite(payload.kgPerLm) ? payload.kgPerLm.toFixed(2) : '';

                        setRunningMeters(lm);
                        setRunningMetersKilogramm(kgLm);

                        message.success('Material-Reservation übernommen.');
                        setMaterialDetailOpen(false);
                    }}
                />

            </Modal>
        </div>
    );
};

export default ColorReservationCreation;